/**
 * DeleteDatabaseConnectionModal Component
 *
 * Modal for confirming database connection deletion.
 * IMPORTANT: Database connection can only be deleted if it has no access grants.
 */

import React, { useState } from 'react';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Alert } from '../../../components/ui/Alert';
import { useDeleteDatabaseConnection } from '../hooks/useDeleteDatabaseConnection';
import { useAuth } from '../../../contexts/AuthContext';
import type { DatabaseConnection } from '../../../types/database-connection.types';

interface DeleteDatabaseConnectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  databaseConnection: DatabaseConnection;
}

/**
 * DeleteDatabaseConnectionModal component
 */
export const DeleteDatabaseConnectionModal: React.FC<DeleteDatabaseConnectionModalProps> = ({
  isOpen,
  onClose,
  databaseConnection,
}) => {
  const { tenant } = useAuth();
  const tenantId = tenant?.tenantId || '';
  const [errorMessage, setErrorMessage] = useState<string>('');

  const hasActiveGrants = (databaseConnection.accessGrants?.length || 0) > 0;

  const { mutate: deleteDatabaseConnection, isPending } = useDeleteDatabaseConnection(tenantId, {
    onSuccess: () => {
      onClose();
    },
    onError: (error) => {
      setErrorMessage(error);
    },
  });

  const handleDelete = () => {
    setErrorMessage('');
    deleteDatabaseConnection(databaseConnection.dbConnectionId);
  };

  const handleClose = () => {
    if (!isPending) {
      setErrorMessage('');
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <Modal.Header>Delete Database Connection</Modal.Header>
      <Modal.Body>
        <div className="space-y-4">
          {/* Error Message */}
          {errorMessage && <Alert variant="error">{errorMessage}</Alert>}

          {/* Warning for active grants */}
          {hasActiveGrants && (
            <Alert variant="warning">
              This database connection has {databaseConnection.accessGrants?.length} active access grant(s).
              You must revoke all access grants before deleting this connection.
            </Alert>
          )}

          <div className="text-sm text-gray-700">
            <p>
              Are you sure you want to delete the database connection{' '}
              <span className="font-medium">{databaseConnection.dbConnectionName}</span>?
            </p>
            <p className="mt-2 text-gray-500">
              Connection code: <span className="font-mono">{databaseConnection.dbConnectionCode}</span>
            </p>
            <p className="mt-4 text-red-600 font-medium">
              This action cannot be undone.
            </p>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={isPending}>
          Cancel
        </Button>
        <Button
          variant="danger"
          onClick={handleDelete}
          loading={isPending}
          disabled={hasActiveGrants}
        >
          Delete Connection
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
